import jobQueueStatusApi from '../../../api/st-job-queue-status';
import { buildReportXml } from '../../Utility';
import { CalendarService } from './CalendarService';

export class AttendanceReportService {
    /**
     * Generate and queue an attendance report for a specific week
     * @param startDate - The start date of the week
     * @param staffSchoolYearGU - The staff school year GUID
     * @param revPersonSignatureGU - The RevPersonSignature GUID for the staff signature
     * @returns Job Queue GUID to monitor with GB system
     */
    async generateWeeklyReport(
        startDate: Date,
        staffSchoolYearGU: string,
        revPersonSignatureGU: string,
        attendanceType: number | undefined
    ): Promise<string> {
        const reportGU = 'AA220B22-E70D-4143-B8BD-51BE5A34A9CA';
        const numWeeks = 1;

        const jobQueueGU = await this.queueReportJob(
            reportGU,
            numWeeks,
            startDate,
            staffSchoolYearGU,
            revPersonSignatureGU,
            attendanceType
        );

        return jobQueueGU;
    }

    private async queueReportJob(
        reportGU: string,
        numWeeks: number,
        startDate: Date,
        staffSchoolYearGU: string,
        revPersonSignatureGU: string,
        attendanceType: number | undefined,
    ): Promise<string> {
        const dateStr = CalendarService.formatDateForApi(startDate);

        // Build report XML
        const xml = buildReportXml({
            reportGUID: reportGU,
            reportName: 'Class Roster',
            reportNumber: 'STU409',
            outputType: 'PDF',
            pageOrientation: 'PORTRAIT',
            maskPhoneNumbers: true,
            showFooterPrintedBy: true,
            parameters: [
                // Staff GUID parameter
                {
                    alias: 'StaffSchoolYearGU',
                    sourceName: 'K12-AttendanceInfo-Reports-ClassDailyAttendanceRI-StaffSchoolYearGU',
                    sourceObject: 'ClassDailyAttendanceRI',
                    sourceObjectGUID: '6235255E-4424-448F-8ADE-46CAEE5526F2',
                    elementName: 'StaffSchoolYearGU',
                    value: staffSchoolYearGU,
                    conditionType: 'EQUAL'
                },
                // Start date parameter
                {
                    alias: 'StartDate',
                    sourceName: 'K12-AttendanceInfo-Reports-DailyClassAttendanceRI-StartDate',
                    sourceObject: 'DailyClassAttendanceRI',
                    sourceObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
                    elementName: 'StartDate',
                    value: dateStr,
                    conditionType: 'EQUAL'
                },
                // Number of weeks parameter
                {
                    alias: 'Weeks',
                    sourceName: 'K12-AttendanceInfo-Reports-DailyClassAttendanceRI-Weeks',
                    sourceObject: 'DailyClassAttendanceRI',
                    sourceObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
                    elementName: 'Weeks',
                    value: numWeeks.toString(),
                    conditionType: 'EQUAL'
                },
                // Period range
                {
                    alias: 'PeriodBegin',
                    sourceName: 'K12-AttendanceInfo-Reports-ClassDailyAttendanceRI-PeriodBegin',
                    sourceObject: 'ClassDailyAttendanceRI',
                    sourceObjectGUID: '6235255E-4424-448F-8ADE-46CAEE5526F2',
                    elementName: 'PeriodBegin',
                    value: '0',
                    conditionType: 'GREATERTHANOREQUAL'
                },
                {
                    alias: 'PeriodEnd',
                    sourceName: 'K12-AttendanceInfo-Reports-ClassDailyAttendanceRI-PeriodEnd',
                    sourceObject: 'ClassDailyAttendanceRI',
                    sourceObjectGUID: '6235255E-4424-448F-8ADE-46CAEE5526F2',
                    elementName: 'PeriodEnd',
                    value: '9',
                    conditionType: 'LESSTHANOREQUAL'
                },
                // Display options
                {
                    alias: 'ShowPeriodAttendance',
                    sourceName: 'K12-AttendanceInfo-Reports-DailyClassAttendanceRI-ShowPeriodAttendance',
                    sourceObject: 'DailyClassAttendanceRI',
                    sourceObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
                    elementName: 'ShowPeriodAttendance',
                    value: attendanceType === 0 ? 'N' : 'Y',
                    conditionType: 'EQUAL'
                },
                {
                    alias: 'AbsenceCodes',
                    sourceName: 'K12-AttendanceInfo-Reports-DailyClassAttendanceRI-AbsenceCodes',
                    sourceObject: 'DailyClassAttendanceRI',
                    sourceObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
                    elementName: 'AbsenceCodes',
                    value: 'Y',
                    conditionType: 'EQUAL'
                },
                {
                    alias: 'IncludeSignature',
                    sourceName: 'K12-AttendanceInfo-Reports-DailyClassAttendanceRI-IncludeSignature',
                    sourceObject: 'DailyClassAttendanceRI',
                    sourceObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
                    elementName: 'IncludeSignature',
                    value: 'Y',
                    conditionType: 'EQUAL'
                },
                // Staff signature GUID parameter
                {
                    alias: 'StaffSignatureGU',
                    sourceName: 'K12-AttendanceInfo-Reports-ClassDailyAttendanceRI-StaffSignatureGU',
                    sourceObject: 'ClassDailyAttendanceRI',
                    sourceObjectGUID: '6235255E-4424-448F-8ADE-46CAEE5526F2',
                    elementName: 'StaffSignatureGU',
                    value: revPersonSignatureGU,
                    conditionType: 'EQUAL'
                }
            ]
        }, window.ST.RevFocusKey);

        // Queue the report job
        const response = await jobQueueStatusApi.QueueReport({
            processStateXml: xml,
            jobID: "STU409",
            focusKey: window.ST.RevFocusKey,
            jobType: 'Reports',
            description: 'Class Roster Report',
            reportNumber: 'STU409',
            primaryBusinessObjectGUID: 'DC59FA37-FCF2-4559-A90E-3AB39FBA591B',
            priority: 1, // JobPriority.High
            outputType: 'PDF',
            notifyAttachment: false
        });

        if (!response.success || !response.jobQueueGUID) {
            throw new Error(response.errorMessage || 'Failed to queue report job');
        }

        return response.jobQueueGUID;
    }

}

// Export singleton instance
export const attendanceReportService = new AttendanceReportService();
